import React, { useState, useEffect, useRef } from 'react';
import { ProfileService } from "../service/ProfileService";
import classNames from 'classnames';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';

import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { AuthorizationService, MANAGE_PROFILES, Permissions } from '../service/AuthorizationService';
import './Crud.scss'
import * as _ from 'lodash';
import { ProgressBar } from 'primereact/progressbar';
import { v4 as uuidv4 } from 'uuid';
import { Paginator } from 'primereact/paginator';
import { ListBox } from 'primereact/listbox';

export function ProfileCrud() {

    let emptyProfile = {
        id: uuidv4(),
        name: '',
        permissions: []
    };

    const [profiles, setProfiles] = useState(null);
    const [profileDialog, setProfileDialog] = useState(false);
    const [deleteProfileDialog, setDeleteProfileDialog] = useState(false);
    const [profile, setProfile] = useState(emptyProfile);
    const [selectedProfiles, setSelectedProfiles] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [hasAccess, setHasAccess] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const toast = useRef(null);
    const dt = useRef(null);
    const profileService = new ProfileService();
    const authorizationService = new AuthorizationService();

    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState('name');
    const [sortOrder, setSortOrder] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [first, setFirst] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const updateList = async () => {
        const data = await profileService.list(first, rowsPerPage, search, sortField, sortOrder);
        setProfiles(data.items);
        setTotalRecords(data.totalRecords)
    }

    const onPageChange = async (pageEvent) => {
        setFirst(pageEvent.first);
        setRowsPerPage(pageEvent.rows);
        const data = await profileService.list(pageEvent.first, pageEvent.rows, search, sortField, sortOrder);
        setProfiles(data.items);
        setTotalRecords(data.totalRecords)
    }

    const doSearch = async () => {
        const data = await profileService.list(first, rowsPerPage, globalFilter, sortField, sortOrder);
        setSearch(globalFilter);
        setProfiles(data.items);
        setTotalRecords(data.totalRecords)
    }

    const onSort = async (sortEvent) => {
        console.log(sortEvent);
        setSortField(sortEvent.sortField);
        setSortOrder(sortEvent.sortOrder);
        const data = await profileService.list(first, rowsPerPage, search, sortEvent.sortField, sortEvent.sortOrder);
        setProfiles(data.items);
        setTotalRecords(data.totalRecords)
    }

    useEffect(() => {
        setHasAccess(authorizationService.hasPermission(MANAGE_PROFILES));

        if(authorizationService.hasPermission(MANAGE_PROFILES)){
            async function fetchData() {
                await updateList();
                setLoading(false);
            }        
            fetchData();
            
        }
    }, []);

    const openNew = () => {
        emptyProfile.id = uuidv4();
        setProfile(emptyProfile);
        setSubmitted(false);
        setProfileDialog(true);
    }

    const hideDialog = () => {
        if(!saving){
            setSubmitted(false);
            setProfileDialog(false);
        }
    }

    const hideDeleteProfileDialog = () => {
        setDeleteProfileDialog(false);
    }

    const saveDisabled = () => {
        return (profile.name || "") === "";
    }

    const saveProfile = async () => {
        try {
            setSaving(true);
            setSubmitted(true);

            if (profile.name.trim()) {
                console.log(profile);
                if (profile.id) {
                    await profileService.update(profile);
                    toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Profile Updated', life: 3000 });
                }
                else {
                    await profileService.create(profile);
                    toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Profile Created', life: 3000 });
                }

                await updateList();
                setProfileDialog(false);
                setProfile(emptyProfile);
                setSaving(false);
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: error.toString(), sticky: true });
            setSaving(false);
            console.log(error);
            throw error;
        }
    }

    const editProfile = (profile) => {
        setProfile({ ...profile });
        setProfileDialog(true);
    }

    const confirmDeleteProfile = (profile) => {
        setProfile(profile);
        setDeleteProfileDialog(true);
    }

    const deleteProfile = async () => {
        try {
            setSaving(true);
            await profileService.delete(profile);
            setProfiles(await profileService.list());
            toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Profile Deleted', life: 3000 });    
        } catch (error) {
            console.log(error);
            toast.current.show({ severity: 'error', summary: 'Error', detail: error.toString(), sticky: true });    
            setSaving(false);
        }

        setDeleteProfileDialog(false);
        setProfile(emptyProfile);
        setSaving(false);
    }

    const onInputChange = (e, name) => {
        const val = (e.target && e.target.value) || '';
        let _profile = { ...profile };
        _.set(_profile, name, val);
        setProfile(_profile);
    }

    const onPermissionChange = (permissions) => {
        let _profile = { ...profile };
        _.set(_profile, "permissions", permissions);
        setProfile(_profile);
    }

    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                <Button label="New" icon="pi pi-plus" className="p-button-success p-mr-2" onClick={openNew} />
            </React.Fragment>
        )
    }

    const actionBodyTemplate = (rowData) => {
        return (
            <React.Fragment>
                <Button icon="pi pi-pencil" className="p-button-rounded p-button-success p-mr-2" onClick={() => editProfile(rowData)} />
                <Button icon="pi pi-trash" className="p-button-rounded p-button-warning" onClick={() => confirmDeleteProfile(rowData)} />
            </React.Fragment>
        );
    }

    const header = (
        <div className="table-header">
            <h5 className="p-m-0">Manage Profiles</h5>
            <span className="p-input-icon-left">
                <i className="pi pi-search" />
                <InputText type="search" onInput={(e) => setGlobalFilter(e.target.value)} placeholder="Search..." onKeyDown={(e) => { if (e.key === 'Enter') { doSearch()}}}/>
            </span>
        </div>
    );

    const profileDialogFooter = (
        <React.Fragment>
            { !saving && <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={hideDialog} />}
            { !saving && <Button disabled={saveDisabled()} label="Save" icon="pi pi-check" className="p-button-text" onClick={saveProfile} />}
            { saving && <ProgressSpinner style={{width: '20px', height: '20px'}} strokeWidth="8"/>}
        </React.Fragment>
    );
    const deleteProfileDialogFooter = (
        <React.Fragment>
            { !saving && <Button label="No" icon="pi pi-times" className="p-button-text" onClick={hideDeleteProfileDialog} />}
            { !saving && <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={deleteProfile} />}
            { saving && <ProgressSpinner style={{width: '20px', height: '20px'}} strokeWidth="8"/>}
        </React.Fragment>
    );

    

    if(hasAccess){

        return (
            <div className="datatable-crud-demo">
                <Toast ref={toast} />
                <div className="card">
                    { loading && <ProgressBar mode="indeterminate"/> }
                    { !loading && <Toolbar className="p-mb-4" left={leftToolbarTemplate}></Toolbar>}

                    { !loading &&
                    <>
                    <DataTable ref={dt} value={profiles} selection={selectedProfiles} onSelectionChange={(e) => setSelectedProfiles(e.value)}
                        dataKey="id"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageProfile RowsPerPageDropdown"
                        currentPageProfileTemplate="Showing {first} to {last} of {totalRecords} profiles"
                        header={header}
                        autoLayout={true}
                        onSort={onSort}
                        sortField={sortField} 
                        sortOrder={sortOrder}
                        >

                        <Column field="name" header="Name" sortable></Column>
                        <Column field="permissions" header="Permissions" body={(data) => data.permissions.map(p => (Permissions.find(x => x.value === p) || {label: "Invalid Permission"}).label).join(", ") }></Column>
                        <Column body={actionBodyTemplate}></Column>
                    </DataTable>

                    <Paginator first={first} rows={rowsPerPage} totalRecords={totalRecords} rowsPerPageOptions={[5, 10, 25, 50, 100]} onPageChange={onPageChange}></Paginator>
                    </>
                    }
                </div>

                <Dialog visible={profileDialog} style={{ width: '90vw' }} header="Profile Details" modal className="p-fluid" footer={profileDialogFooter} onHide={hideDialog}>
                    {profile.image && <img src={`assets/demo/images/profile/${profile.image}`} onError={(e) => e.target.src = 'https://www.primefaces.org/wp-content/uploads/2020/05/placeholder.png'} alt={profile.image} className="profile-image" />}
                    <div className="p-field">
                        <label htmlFor="name">Name*</label>
                        <InputText disabled={saving} id="name" value={profile.name} onChange={(e) => onInputChange(e, 'name')} required autoFocus className={classNames({ 'p-invalid': submitted && !profile.name })} />
                        {submitted && !profile.name && <small className="p-invalid">Name is required.</small>}
                    </div>
                    <div className="p-field">
                        <label htmlFor="permissions">Permissions</label>
                        <ListBox multiple="true" value={profile.permissions} options={Permissions} onChange={(e) => onPermissionChange(e.value)} />
                    </div>

                </Dialog>

                <Dialog visible={deleteProfileDialog} style={{ width: '450px' }} header="Confirm" modal footer={deleteProfileDialogFooter} onHide={hideDeleteProfileDialog}>
                    <div className="confirmation-content">
                        <i className="pi pi-exclamation-triangle p-mr-3" style={{ fontSize: '2rem' }} />
                        {profile && <span>Are you sure you want to delete <b>{profile.name}</b>?</span>}
                    </div>
                </Dialog>
            </div>
        );
    }
    else {
        return (
            <div className="datatable-crud-demo">
                <div className="card">
                    <b>Permission Denied</b>
                </div>
            </div>
        );
    }
}